/**
 * Виджеты, которые появились в расширении в версии 4.0.0: Daily focus,
 * News, 1000 interesting facts, Chess puzzle.
 *
 * Факты — только из data/screenshots/report-4-new-widgets.md (§4.1–4.4),
 * у каждого там ссылка на код mavo-dash@7193681. Запросы под страницы —
 * docs/seo/research/README.md, §5.
 *
 * Скриншотов пока нет: блок screenshot добавляется, когда кадры из
 * task_screenshots.md лягут в public/img/widgets.
 *
 * @see docs/SEO_WRITER.md
 */

export default [
	{
		slug: 'daily-focus',
		name: 'Daily Focus',
		category: 'Productivity',
		plan: 'free',
		model: 'DailyFocusModel',
		title: 'Daily Focus: Set an Intention for the Day',
		metaDescription:
			'Set one intention for the day and see it on every new tab. Tick it off for a burst of confetti, keep a streak, carry an unfinished focus into tomorrow.',
		h1: 'Daily focus, one intention for the day on every new tab',
		lead:
			'Daily focus asks a single question when you open a new tab: what’s your main focus for today? Type your intention for the day, press Enter, and it stays in front of you on every tab until you tick it off. One line, not a to-do list. There’s room for exactly one thing, and that’s what makes it useful.',
		screenshot: {
			file: 'daily-focus-hero.webp',
			alt:
				'Daily focus greeting Good morning, Alex with the focus Finish the budget draft typed in and a 5-day streak below',
			caption:
				'Good morning, one focus for the day, and a streak worth keeping',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Why one intention beats a list',
				p: [
					'A list of twelve tasks tells you everything and ranks nothing. Setting one intention for the day forces the choice you’ve been dodging. Out of all of it, what would make today count? You answer once in the morning, and the tab asks again every time you drift off to check something.',
					'Make it concrete. “Finish the budget draft” works, “be productive” doesn’t. “Call the landlord before noon” beats “sort out the flat”. If you can’t tell at six in the evening whether you did it, it was a mood rather than a focus.',
				],
			},
			{
				h2: 'Ticking it off, and keeping a streak',
				p: [
					'When it’s done, hit the round Mark as done button. You get a small burst of confetti and a “Nice work!” or a “Well done!”. It’s silly, and it still feels good at the end of a long day.',
					'Finished days add up to a streak, and a row of dots shows the last seven days at a glance. The streak holds while your last finished day is today or yesterday. Miss a whole day and it starts again from one.',
				],
				image: {
					file: 'daily-focus-done.webp',
					alt:
						'Daily focus card with Finish the slides ticked off, confetti in the air, Nice work! and a 6-day streak',
					caption: 'Tick it off and the confetti does the rest',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'What happens at midnight',
				p: [
					'Nothing gets wiped. The widget notices the date has changed and asks the question again, and yesterday stays in the history. The greeting follows the clock as well. Good morning until noon, good afternoon until six, good evening until eleven, and after that it says hello to the night owl.',
					'If yesterday’s focus didn’t get done, it offers to carry it over. It looks back up to three days, so a Friday job you didn’t finish turns up again on Monday instead of disappearing over the weekend. Say “No, thanks” and you start fresh.',
				],
			},
			{
				h2: 'Five designs, from quiet to loud',
				p: [
					'Headline, the default, writes your focus in big thin letters straight onto the wallpaper with no card behind it. Glass sets it on a see-through card. Minimal squeezes everything into a single line, for people who want a reminder rather than a banner. Sticky note is handwritten on yellow, and Gradient is the one you can’t miss.',
					'On a light, plain background the text turns dark by itself. You don’t have to fiddle with colours to make it readable.',
				],
				image: {
					file: 'daily-focus-designs.webp',
					alt:
						'Daily focus settings with live previews of five designs: Headline, Glass, Minimal, Sticky note and Gradient',
					caption: 'Five designs, each previewed with your own focus',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'What it won’t do',
				p: [
					'It will not nag. There are no reminders and no notifications; it only shows up when you open a tab, which for most people is plenty. It doesn’t talk to your task list or habit tracker either. It’s a separate widget with its own memory, and that memory goes back 30 days.',
					'Publishing your page as a theme is safe. Your name and your past focuses are stripped out, so whoever installs it gets their own blank question.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Headline, Glass, Minimal, Sticky note or Gradient. Minimal is one line, so it drops the greeting and the week of dots.',
			},
			{
				name: 'Show greeting and Your name',
				detail:
					'The first time, it asks what to call you, and Skip is fine. The name only appears in the greeting.',
			},
			{
				name: 'Show streak',
				detail: 'The count of finished days in a row. On by default.',
			},
			{
				name: 'Show the last 7 days',
				detail:
					'A dot per day, so a patchy week is obvious at a glance.',
			},
			{
				name: 'Offer to carry over an unfinished focus',
				detail:
					'Brings back the last unfinished focus from up to three days ago. Turn it off if you’d rather every day start blank.',
			},
			{
				name: 'Clear history',
				detail:
					'Deletes the streak and past days. Ctrl+Z brings them back if you clicked it by accident.',
			},
		],
		useCases: [
			'Deep-work days with one deliverable and nothing else on the tab.',
			'Exam season, with one topic per day instead of the whole syllabus.',
			'Freelancing, where today’s focus is simply today’s client.',
			'A morning intention habit without a separate journaling app.',
			'Next to a pomodoro timer, so the timer knows what it’s timing.',
		],
		faq: [
			{
				q:
					'How can I set an intention for the day and keep it visible?',
				a:
					'Write one short, concrete intention in the morning and put it where your eyes land many times a day, like a sticky note on the monitor or your browser’s new tab. On the new tab, Daily focus asks “What is your main focus for today?”, saves your answer when you press Enter and shows it on every tab until you mark it done.',
			},
			{
				q: 'What are examples of intentions for the day?',
				a:
					'Intentions for the day can be tasks or attitudes, as long as you’ll know by evening whether you kept them, like “Send the proposal before lunch”, “Two hours on chapter three, phone in another room” or “Listen more than I talk at the 3 pm meeting”. “Be productive” is too vague to tick off. Daily focus holds up to 120 characters, enough for one clear sentence.',
			},
			{
				q: 'Is there a Chrome extension for ADHD and focus?',
				a:
					'There’s no Chrome extension for ADHD as such, but several help with focus in different ways, from site blockers that cut off distractions to timers and new tab pages that keep one priority in view. Some people with ADHD find a single visible task easier to act on than a long list. New Tab Studio’s Daily focus works that way, showing one focus on every new tab until it’s done.',
			},
			{
				q: 'Is the Daily focus widget free?',
				a:
					'Yes, Daily focus is free, including all five designs, the streak and the carry-over. Your page can hold three widgets of your own on the free plan, and this would be one of them. It never goes online either, so your focus, your streak and your name stay in this browser.',
			},
			{
				q: 'Can I set more than one focus a day?',
				a:
					'No, Daily focus holds one focus per day, because a second one would turn it back into a list. If you need a list as well, put a to-do list next to it and keep the focus for the item that matters most. There are no reminders or notifications; it shows up whenever you open a tab.',
			},
		],
		related: ['todo-list', 'habit-tracker', 'pomodoro', 'quotes'],
		keywords: [
			'intention for the day',
			'set an intention for the day',
			'daily focus',
			'main focus for today',
		],
	},
	{
		slug: 'news',
		name: 'News',
		category: 'Content',
		plan: 'free',
		model: 'NewsFeedModel',
		title: 'News Widget for Your Chrome New Tab',
		metaDescription:
			'Headlines from 62 sources in 10 languages, right on your new tab. Pick a feed, choose one of five layouts, and still see the last headlines offline.',
		h1: 'News widget for your Chrome new tab',
		lead:
			'The news widget puts headlines from a source you choose on your Chrome new tab, so the day’s stories are there before you’ve typed a thing. There are 62 feeds in 10 languages, five layouts, and one click on a headline opens the article.',
		screenshot: {
			file: 'news-hero.webp',
			alt:
				'News widget with seven NASA stories as photo cards in three columns over a starry lake',
			caption:
				'NASA headlines as cards: one big story on top, six more below',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'Picking a source',
				p: [
					'Add the widget and a Choose a news source window opens straight away. Click a source and the window closes by itself as soon as the headlines arrive. You can narrow the list by topic: Top stories, AI, Technology, Programming, Science, Space, Business, Sports, Health, Culture and Games.',
					'In English that means DW News, The New York Times and ABC News for the day’s stories, WIRED and Hacker News for tech, DEV Community for programming, NASA and Spaceflight News for space. The AI topic mixes Hugging Face’s model and paper feeds with a Hacker News feed filtered down to AI stories that already have 80 points or more.',
					'Pick a topic and you’ll see feeds in every language, starting with your interface language and then English. Handy if you’re learning German or Spanish and want tagesschau or El País in front of you every morning.',
				],
				image: {
					file: 'news-picker.webp',
					alt:
						'Choose a news source dialog with topic chips and technology feeds grouped by language',
					caption:
						'Pick a topic, then a feed: here, technology news in several languages',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'One widget, one feed',
				p: [
					'Each widget shows a single source. Want tech and sport? Add two widgets. It keeps things tidy — and it means no mixed river of everything.',
					'You also can’t paste in your own RSS link. The list is fixed, and only feeds that let a browser read them directly made it in. That’s why a favourite site might be missing. The upside is that headlines travel straight from the publisher to your browser, with no server of ours in between.',
				],
			},
			{
				h2: 'Five layouts',
				p: [
					'List is the default: headlines stacked with small pictures. Cards spreads them into two or three columns once the widget is wide enough. Headline shows one story at a time, turns to the next every eight seconds and stops while your mouse is over it. Glass is the fifth, and News ticker squeezes everything into one thin strip.',
					'Pictures can be switched off in every layout except the ticker, which has none anyway.',
				],
				image: {
					file: 'news-headline.webp',
					alt:
						'Morning new tab with a clock, the date and one large NASA story with a photo of star trails over Earth',
					caption:
						'One story at a time, the next one after eight seconds',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Fresh enough, and fine offline',
				p: [
					'The last batch of headlines (up to 20) is saved with the widget, so a new tab shows them instantly, before anything has loaded. Updates happen at most every 20 minutes, a tab sitting in the background doesn’t fetch at all, and there’s a Refresh button when you can’t wait.',
					'Lose the connection and the saved headlines stay, with a small note in the corner saying how old they are. Share your page as a theme and the saved headlines stay behind; whoever installs it loads their own.',
					'From version 4.0.1 every story has a × to hide it, with a few seconds to undo, and Mark all as read in the header dims what you’ve already seen. Prefer read stories gone rather than dimmed? Switch Read stories from Dim to Hide.',
				],
				image: {
					file: 'news-read.webp',
					alt:
						'News list with two dimmed read stories, a cursor on a hide button and a Story hidden, Undo toast',
					caption:
						'Read stories fade out, hidden ones step aside, and Undo is right there',
					width: 1440,
					height: 900,
					small: true,
				},
			},
		],
		settings: [
			{
				name: 'Source',
				detail:
					'62 feeds with topic chips on top. Changing it later is a double-click on the widget away.',
			},
			{
				name: 'Design',
				detail: 'List, Cards, Headline, Glass or News ticker.',
			},
			{
				name: 'Number of headlines',
				detail:
					'3, 5, 8, 10, 15 or 20. Ten by default, which fills a medium widget without scrolling.',
			},
			{
				name: 'Show images',
				detail:
					'On by default. Turning it off gives you a denser list and a calmer page.',
			},
			{
				name: 'Open links in a new tab',
				detail:
					'On by default, so the page with your widgets stays where it was.',
			},
		],
		useCases: [
			'Hacker News or DEV Community on a developer’s dashboard.',
			'A Headline card beside the clock for a ten-second morning skim.',
			'Language practice with DW, franceinfo or El País in the language you’re learning.',
			'NASA and Spaceflight News for anyone who follows launches.',
			'Business headlines on a work workspace, nothing at all on the personal one.',
		],
		faq: [
			{
				q: 'Can I see news headlines on my Chrome new tab?',
				a:
					'Chrome on a computer has no news feed on its standard new tab, though the Android and iPhone apps show the Discover feed under the search box. On desktop it takes an extension such as New Tab Studio, whose News widget shows headlines from one of 62 feeds in 10 languages as a list, cards, a single rotating headline or a ticker.',
			},
			{
				q: 'How do I turn off the news feed on Chrome’s new tab?',
				a:
					'On Chrome for Android or iPhone, the stories under the search box are the Discover feed, and the menu next to the Discover heading can turn it off. Desktop Chrome has no feed of its own, so headlines there come from an extension. To remove the News widget, select it in edit mode and press Delete; from version 4.0.1 single stories can be hidden too.',
			},
			{
				q: 'Can I add my own RSS feed?',
				a:
					'No, the News widget only works with its built-in list of 62 feeds, and an unknown address is not accepted. Only feeds a browser can read directly made the list, so a favourite site may be missing. Each widget shows one source, so for tech and sport side by side, add two widgets.',
			},
			{
				q: 'Will the news widget slow down my new tab?',
				a:
					'The news widget shouldn’t slow your new tab, because it shows the last saved headlines, up to 20, before the network has answered. Updates come at most every 20 minutes and a tab in the background doesn’t fetch at all. Offline, the saved headlines stay put, labelled with when they were last updated.',
			},
			{
				q: 'Is the News widget free?',
				a:
					'Yes, the News widget is free with all 62 sources and five layouts, and headlines come straight from each publisher instead of through a server of ours. The one catch is the general cap — three hand-placed widgets on the free plan.',
			},
		],
		related: ['facts', 'weather', 'search-bar', 'quotes'],
		keywords: [
			'news widget',
			'news widget for chrome',
			'news on new tab',
			'chrome news feed',
		],
	},
	{
		slug: 'facts',
		name: 'Interesting Facts',
		category: 'Content',
		plan: 'free',
		model: 'FactsModel',
		title: 'Fact of the Day on Your Chrome New Tab',
		metaDescription:
			'A new fact on every tab, or once a day if you prefer. 1,000 facts in English across 12 categories, your own facts, likes, and no internet needed.',
		h1: 'A fact of the day on every Chrome new tab',
		lead:
			'This widget gives you a fact of the day on your new tab, or a fresh one each time you open a tab if you can’t wait. It carries 1,000 interesting facts in English, sorted into 12 categories from space to food, and it doesn’t need the internet to show them.',
		screenshot: {
			file: 'facts-hero.webp',
			alt:
				'Two fact cards: a glass one about Saturn floating in water and an index card about the three hearts of an octopus',
			caption:
				'Saturn would float in a big enough bathtub. An octopus has three hearts',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'How often it changes',
				p: [
					'You choose the pace: every new tab, every 5 minutes, every hour, every 6 hours (the default), once a day or once a week. Set it to Day and it’s a proper fact of the day. Set it to New tab and it becomes a small slot machine you’ll pull more often than you’d admit.',
					'The card has its own buttons: like, previous, next, pin and copy. Pin keeps a good one on screen until you let it go. Copy is for the fact you’re about to send to a friend.',
				],
			},
			{
				h2: 'What’s in there',
				p: [
					'Twelve categories, roughly 83 facts each in English: Space, Science, Animals, Nature & Earth, Human body, History, Countries & places, Technology & inventions, Food & drink, Language & words, Art, music & film, and Sport & games. Untick the ones you don’t care about and a counter shows how many facts are left in the rotation.',
					'One honest note. Facts don’t come with a source attached, so if one sounds too good to be true, look it up before you repeat it at dinner.',
				],
				image: {
					file: 'facts-settings.webp',
					alt:
						'Facts settings dialog with twelve category chips, a rotation counter and fact change intervals',
					caption:
						'Twelve categories, 1,000 facts, and you pick how often they change',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Your own facts, and the ones you liked',
				p: [
					'My facts is a short editor for your own (add, change, delete). Keep them on their own and the widget becomes a rotating card of things you want to remember, from formulas to the birthdays you keep forgetting.',
					'Liked facts collects everything you’ve hearted, shared by all the facts widgets on your page and kept apart from liked quotes. And if you run two facts widgets side by side, each gets its own order, so they don’t keep showing the same thing.',
				],
			},
			{
				h2: 'Languages',
				p: [
					'Facts follow the language of the extension. English and Russian have the full thousand. German, Spanish, French, Italian, Portuguese, Turkish, Japanese and Chinese have 200 each, the first ones from every category, and the same fact has the same number in every language.',
					'If a language pack fails to load for some reason, the widget falls back to English rather than showing an empty card.',
				],
			},
			{
				h2: 'Six looks',
				p: [
					'Glass is the default. The others are Light, Dark, Index card (handwritten, like a revision card), Minimal and Vivid. Text size goes from Small to Extra large, and the “Did you know?” label and the category name can each be hidden.',
					'Not to be confused with the Interesting fact tile in the IFrame group, which loads a web page from another site. This one lives inside the extension.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail: 'Glass, Light, Dark, Index card, Minimal or Vivid.',
			},
			{
				name: 'Categories',
				detail:
					'Twelve topics plus My facts and Liked facts, with a live count of what’s in the rotation.',
			},
			{
				name: 'Change the fact',
				detail:
					'New tab, 5 min, Hour, 6 hours, Day or Week. Six hours unless you change it.',
			},
			{
				name: 'Pin the current fact',
				detail: 'Stops the rotation on the fact you’re looking at.',
			},
			{
				name: 'Text size',
				detail:
					'Small, Medium, Large or Extra large, on top of the size the design sets.',
			},
			{
				name: 'Show the “Did you know?” label and the category',
				detail: 'Two switches, both on by default.',
			},
		],
		useCases: [
			'A classroom or family computer where kids open tabs all day.',
			'Something small to read while a slow page loads.',
			'Your own facts as flashcards without the flashcard ceremony.',
			'Trivia-night warm-up, one tab at a time.',
		],
		faq: [
			{
				q:
					'Is there a Chrome extension that shows a fun fact on every new tab?',
				a:
					'Yes, New Tab Studio has a facts widget that shows a fun fact on every new tab, or once a day if you’d rather have a proper fact of the day. In English it carries 1,000 facts across 12 categories, from space to food. Look for it as 1000 interesting facts in the Common group of the widget menu.',
			},
			{
				q: 'What is a fun fact of the day?',
				a:
					'A fun fact of the day is one short, surprising and true thing to learn each day. Here’s one from the widget’s Animals category. Resting sea otters often wrap themselves in long strands of kelp so they don’t drift away while they sleep. Set Change the fact to Day and the widget picks a new one every day.',
			},
			{
				q: 'Is the fact of the day widget free?',
				a:
					'Yes, the fact of the day widget is free with nothing held back, so all 12 categories, all six designs, My facts and Liked facts work on the free plan. The widget only counts toward that plan’s limit of three widgets you add yourself; anything installed with a theme isn’t counted.',
			},
			{
				q: 'Does the facts widget need the internet?',
				a:
					'No, the facts widget works offline, since every fact ships inside the extension and the widget makes no network requests. It asks for no permissions either. A plane, a train with patchy Wi-Fi or a laptop in airplane mode all get their next fact right on time.',
			},
			{
				q: 'Are there 1,000 facts in every language?',
				a:
					'No, the facts widget has the full 1,000 facts only in English and Russian. German, Spanish, French, Italian, Portuguese, Turkish, Japanese and Chinese have 200 each, the first ones from every category, and the widget follows the language of the extension. If a language pack fails to load, it falls back to English.',
			},
		],
		related: ['quotes', 'news', 'chess-puzzle'],
		keywords: [
			'fact of the day',
			'random fact of the day',
			'fun fact of the day',
			'random facts new tab',
		],
	},
	{
		slug: 'chess-puzzle',
		name: 'Chess Puzzle',
		category: 'Content',
		plan: 'free',
		model: 'ChessPuzzleModel',
		title: 'Daily Chess Puzzle on Your Chrome New Tab',
		metaDescription:
			'Solve lichess puzzles right on your new tab: the daily puzzle first, then five difficulty levels from mate in one to endgames. Hints, streaks, no sign-up.',
		h1: 'A daily chess puzzle on your Chrome new tab',
		lead:
			'This widget puts the lichess daily chess puzzle on your new tab and lets you solve it on its own board. No site to open, no account to create. Crack it, ask for the next one, or leave it for later and pretend you were working.',
		screenshot: {
			file: 'chess-puzzle-hero.webp',
			alt:
				'Chess puzzle widget showing the puzzle of the day after a correct first move, next to a clock',
			caption: 'One right move down, the rest of the combination to go',
			width: 1440,
			height: 900,
			small: true,
		},
		sections: [
			{
				h2: 'How it plays',
				p: [
					'The first puzzle of the day is the lichess puzzle of the day, with the board turned to your side. Move by dragging a piece or by clicking the piece and then the square. The opponent’s reply plays itself.',
					'A wrong move gets “Not the best move. Try again”, and you can keep trying. A move counts if it matches the solution or if it mates on the spot, the same rule lichess uses. Stuck? Hint lights up the square of the piece that should move, and Solution shows the answer. After that it’s Retry or Next puzzle.',
				],
			},
			{
				h2: 'Difficulty and puzzle types',
				p: [
					'Five levels, from Easiest to Hardest. They apply to the puzzles after the daily one, which is the same for everybody. Puzzle type narrows things down further: Any, Mate in 1, Mate in 2, Mate in 3, Endgame, Opening, or Short for two-move puzzles when you have thirty seconds between meetings.',
					'The theme of a puzzle (Fork, Pin, Back rank mate and so on) only appears after you solve it. Show it earlier and it would give the answer away.',
				],
			},
			{
				h2: 'Streaks, counted honestly',
				p: [
					'Solve one cleanly and your streak goes up. Solve it after a mistake or with a hint and you get “Solved with a mistake” or “Solved with a hint”, and the streak goes back to zero. Showing the solution resets it too. Settings keep your totals, puzzles solved and best streak.',
					'A detail for people east of Greenwich. The lichess daily puzzle changes at midnight UTC, so right after your local midnight you might see yesterday’s again. The widget won’t count it twice.',
				],
				image: {
					file: 'chess-puzzle-solved.webp',
					alt:
						'Wooden chess puzzle widget showing Solved with the Fork theme tag and a streak of five',
					caption:
						'Solved: it was a fork. The theme only appears after you solve it',
					width: 1440,
					height: 900,
					small: true,
				},
			},
			{
				h2: 'Where the puzzles come from',
				p: [
					'Every puzzle comes from lichess.org’s public puzzle service, which needs no key and no login. The widget sends no cookies with those requests. There’s no puzzle collection inside the extension, so a new puzzle needs a connection; the one you’re on is saved and still shows offline.',
					'Moves are checked by the widget’s own chess engine, which knows castling, en passant, promotion, check and mate. An illegal move simply doesn’t happen.',
				],
			},
			{
				h2: 'What it doesn’t do',
				p: [
					'It’s puzzles only. You cannot play a full game here. Progress inside a puzzle isn’t saved, so closing the tab halfway through means starting again. When a pawn promotes, the widget picks the piece from the solution instead of asking you. And it’s silent, with no move sounds at all.',
				],
			},
		],
		settings: [
			{
				name: 'Design',
				detail:
					'Glass, Wood, Light or Midnight. Glass is what a new widget starts with.',
			},
			{
				name: 'Difficulty',
				detail:
					'Easiest, Easier, Normal, Harder or Hardest. Normal by default; the daily puzzle ignores it.',
			},
			{
				name: 'Puzzle type',
				detail:
					'Any, Mate in 1, Mate in 2, Mate in 3, Endgame, Opening or Short (2 moves).',
			},
			{
				name: 'Start each day with the puzzle of the day',
				detail:
					'On by default. Switch it off to go straight to puzzles at your level.',
			},
			{
				name: 'Show coordinates',
				detail:
					'Letters and numbers around the board. Useful when you’re learning notation.',
			},
		],
		useCases: [
			'The daily puzzle as a five-minute warm-up before work.',
			'Mate in 1 and Mate in 2 on a kid’s computer while they learn.',
			'Endgame drills, one position per new tab.',
			'A short brain break between pomodoros.',
		],
		faq: [
			{
				q: 'Is there a Chrome extension with a daily chess puzzle?',
				a:
					'Yes, New Tab Studio’s Chess puzzle widget puts the lichess puzzle of the day on your Chrome new tab and lets you solve it on its own board, with no account. After the daily one you get more at your chosen level, with Hint and Solution for when you’re stuck. Lichess.org and Chess.com also post a daily puzzle on their own sites.',
			},
			{
				q: 'Which chess puzzles are best for improving?',
				a:
					'The chess puzzles that teach most are a little above your comfort level and get solved all the way through before you check the answer. Short tactics — forks, pins, mates in one or two — build pattern recognition, and endgames teach technique. The widget has five difficulty levels and types from Mate in 1 to Endgame, and it names the theme only after you solve.',
			},
			{
				q: 'Do I need a lichess account?',
				a:
					'You don’t need a lichess account, because the Chess puzzle widget uses lichess.org’s public puzzle service without signing in and sends no cookies with those requests. That also means solving here doesn’t move your lichess rating. The widget keeps its own score instead, puzzles solved and best streak, and a hint or a mistake resets the streak.',
			},
			{
				q: 'Does the chess puzzle work offline?',
				a:
					'The Chess puzzle widget works offline only for the puzzle you’re on, which is saved with the widget. There’s no puzzle collection inside the extension, so the next one waits for a connection to lichess.org. Progress inside a puzzle isn’t saved either, and closing the tab halfway means starting that puzzle again.',
			},
			{
				q: 'Is the chess puzzle widget free?',
				a:
					'Yes, the chess puzzle widget is free, every difficulty level and all four board designs included, and lichess doesn’t charge for its puzzles either. On the free plan it does occupy one of your three self-added widgets, the same as a clock or a note would.',
			},
		],
		related: ['facts', 'pomodoro', 'daily-focus'],
		keywords: [
			'daily chess puzzle',
			'chess puzzle chrome extension',
			'chess extension',
			'chess puzzle new tab',
		],
	},
];
